import { type ReactNode, useState } from "react";
import { useFieldContext } from "./form-context";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { cn, sanitizeHtml } from "@/lib/utils";
import { CircleAlert, Redo2, Eye, EyeOff } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "../ui/button";
import { OverridesButton } from "../overrides-button";

export function FormTextField({
  label,
  id,
  description,
  type = "text",
  placeholder,
  readOnly = false,
  autoComplete,
  error,
  onValueChange,
  value,
  htmlContent,
  defaultSystemValue,
  required = false,
  showPasswordToggle = true,
  isSystemSettings = false,
  openOverridesModal,
  domain,
}: {
  label: ReactNode;
  id: string;
  readOnly?: boolean;
  description?: string;
  placeholder?: string;
  autoComplete?: string;
  type?: "text" | "password" | "email" | "number" | "url";
  showPasswordToggle?: boolean;
  min?: number;
  error?: string;
  onValueChange?: (value: string) => void;
  value?: string;
  htmlContent?: string;
  defaultSystemValue?: string;
  required?: boolean;
  isSystemSettings?: boolean;
  openOverridesModal?: () => void;
  domain?: string;
}) {
  const sanitizedHtml = htmlContent ? sanitizeHtml(htmlContent) : null;
  const [showPassword, setShowPassword] = useState(false);
  let field: ReturnType<typeof useFieldContext<string>> | null = null;
  try {
    field = useFieldContext<string>();
  } catch (e) {
    field = null;
  }

  if (!field && typeof value === "undefined") {
    return (
      <div className="flex flex-col gap-2 w-full">
        {label && <div className="text-sm leading-none font-medium">{label}</div>}
        <div className="text-sm text-red-500">
          FormTextField must be used inside a form field context or provided a value/onValueChange prop.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center gap-2">
        {label && <Label htmlFor={id}>{label}</Label>}
        {defaultSystemValue !== undefined && value !== defaultSystemValue && (
          <Tooltip>
            <TooltipTrigger asChild>
              <CircleAlert className="w-4 h-4 text-orange-500" />
            </TooltipTrigger>
            <TooltipContent>
              <p>This value overwrites the default system value: {defaultSystemValue}</p>
            </TooltipContent>
          </Tooltip>
        )}
        {defaultSystemValue !== undefined && value !== defaultSystemValue && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                className="text-orange-500 pb-0 pt-0 mr-auto"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  field?.setValue(defaultSystemValue ?? "");
                }}
              >
                <Redo2 className="w-2 h-2" />
                Reset to default
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Reset the value to the default system value: {defaultSystemValue}</p>
            </TooltipContent>
          </Tooltip>
        )}
        {isSystemSettings && domain !== "SYSTEM" && <OverridesButton onClick={openOverridesModal!} />}
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            type={type === "password" && showPassword ? "text" : type}
            id={id}
            readOnly={readOnly}
            autoComplete={autoComplete ?? (type === "password" ? "new-password" : undefined)}
            value={field ? (field.state.value ?? "") : (value ?? "")}
            onChange={(e) => {
              if (typeof onValueChange === "function") {
                onValueChange(e.target.value);
              } else if (field) {
                field.handleChange(e.target.value);
              }
            }}
            placeholder={placeholder}
            required={required}
            className={cn(
              readOnly && "bg-gray-100 text-gray-500 dark:bg-gray-600 dark:text-gray-300 cursor-not-allowed",
              type === "password" && "pr-10"
            )}
          />
          {type === "password" &&
            field?.state.value !== undefined &&
            field?.state.value !== "" &&
            field?.state.value !== null && (
              <button
                type="button"
                className="absolute inset-y-0 right-3 flex items-center text-gray-500"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
            )}
        </div>
      </div>
      {description && <div className="text-xs form-description">{description}</div>}
      {htmlContent && (
        <div className="text-xs form-description" dangerouslySetInnerHTML={{ __html: sanitizedHtml || "" }} />
      )}
      {error && <div className="text-sm text-red-500">{error}</div>}
    </div>
  );
}
